import React, { useEffect } from "react";
import { Box, Heading, Text, VStack, Link } from "@chakra-ui/react";
import Navbar from "../components/Navbar";
import darkLogo from "../assets/images/Newlogo-black.png";
import BackToTopButton from "../components/common/BackToTopButton";
import Footer from "./Footer";
import JoinCommunitySection from "./common/JoinCommunitySection";
import { Helmet } from "react-helmet-async";

const TermsOfServiceNL = () => {
  // ⬇️ Scroll to the very top every time this page mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <>
      <Helmet>
        <title>Algemene Voorwaarden&nbsp;| NOMAD360</title>
      </Helmet>

      <Box position="absolute" top={0} left={0} width="100%" zIndex="2">
        <Navbar
          bgColor="white"
          linkColor="black"
          styling="outline"
          logoSrc={darkLogo}
          languageCodeColor="black"
          userProfileBgColor="transparent"
        />

        <Box
          maxW={{ base: "100vw", sm: "85vw", md: "85vw", lg: "60vw" }}
          mx="auto"
          px={4}
          py={8}
          pt={8}
        >
          {/* Main Title */}
          <Heading as="h1" size="xl" align="center" mt={10} mb={10}>
            ALGEMENE GEBRUIKS- EN LEVERINGS- VOORWAARDEN NOMAD360
          </Heading>

          <VStack align="start" spacing={3}>
            {/* Introductory Information */}

            <Text mb={0} fontStyle="italic">
              Deze algemene voorwaarden bestaan uit de volgende hoofdstukken:
            </Text>
            <VStack align="start" spacing={1}>
              <Text mb={0}>1. Algemene bepalingen</Text>
              <Text mb={0}>2. Gebruiksvoorwaarden NOMAD360</Text>
              <Text mb={0}>
                3. Specifieke bepaling ten aanzien van Input en Content
              </Text>
            </VStack>
            <Text mb={0}>NOMAD360 wordt aangeboden door:</Text>
            <Text fontStyle="italic" mb={0}>
              <strong>Lancewood AI Solutions B.V.</strong>
              <br />
              Westplein 12
              <br />
              3016 BM Rotterdam
              <br />
              Nederland
              <br />
              KvK-nummer: 95481214
              <br />
              Btw-nummer: NL867147507B01
              <br />
              info@lancewood.nl
            </Text>
            <Text>
              Deze algemene voorwaarden zijn voor het laatst aangepast op: 14
              april 2025
            </Text>

            {/* HOOFDSTUK 1 */}
            <Heading as="h2" size="lg" mt={6}>
              HOOFDSTUK 1 – ALGEMENE BEPALINGEN
            </Heading>

            {/* 1. Toepasselijkheid */}
            <Heading as="h3" size="md">
              1. Toepasselijkheid van deze algemene voorwaarden
            </Heading>
            <Text>
              1.1. Deze algemene voorwaarden zijn van toepassing op alle
              diensten die Lancewood AI Solutions aanbiedt via NOMAD360.
              Specifieke voorwaarden met betrekking tot het gebruik van NOMAD360
              en Input en Content zijn uitgewerkt in respectievelijk Hoofdstuk
              II – Gebruiksvoorwaarden NOMAD360 en Hoofdstuk III – Specifieke
              bepalingen ten aanzien van Input en Content.
            </Text>
            <Text>
              1.2. Door het gebruik van NOMAD360, verklaart Gebruiker akkoord te
              gaan met deze algemene voorwaarden. Afwijkingen van of
              aanvullingen op deze algemene voorwaarden zijn alleen geldig
              indien schriftelijk bevestigd door Lancewood AI Solutions.
            </Text>
            <Text>
              1.3. Eventuele algemene inkoop- en/of andere algemene voorwaarden
              van Gebruiker worden uitdrukkelijk door Lancewood AI Solutions van
              de hand gewezen.
            </Text>
            <Text>
              1.4. Indien één of meer bepalingen van deze algemene voorwaarden
              onverbindend of ongeldig blijken te zijn, blijven de overige
              bepalingen onverkort van kracht.
            </Text>

            {/* 2. Definities */}
            <Heading as="h3" size="md">
              2. Definities
            </Heading>
            <Text>
              2.1. De met een beginhoofdletter aangeduide begrippen in deze
              algemene voorwaarden kunnen zowel in enkelvoud als in meervoud
              worden gebruikt en hebben de volgende betekenis:
            </Text>
            <Text>
              <strong>Account:</strong> het persoonlijk gebruikersprofiel dat de
              Gebruiker aanmaakt met een e-mailadres en wachtwoord, of via het
              inloggen met een Google-account, om toegang te krijgen tot een
              Gebruikers Omgeving binnen NOMAD360;
            </Text>
            <Text>
              <strong>AI-model:</strong> het door Lancewood AI Solutions
              gebruikte Large Language Model (LLM) dat met behulp van
              kunstmatige intelligentie Content genereert op basis van de Input
              van de Gebruiker en gegevens van externe databronnen, zoals
              databrokers;
            </Text>
            <Text>
              <strong>Content:</strong> alle gegevens, informatie, materialen of
              andere inhoud die door of via NOMAD360 zijn gegenereerd of bewerkt
              al dan niet op basis van de door de Gebruiker verstrekte Input.
              Content omvat, maar is niet beperkt tot, de Input van de
              Gebruiker, gegevens verstrekt door derden, en de door het AI-model
              gegenereerde of geoptimaliseerde informatie;
            </Text>
            <Text>
              <strong>Gebruiker:</strong> de natuurlijke persoon die
              gebruikmaakt van NOMAD360 en over een Account beschikt;
            </Text>
            <Text>
              <strong>Gebruikers Omgeving:</strong> een besloten deel van
              NOMAD360 waartoe (uitsluitend) de Gebruiker toegang heeft;
            </Text>
            <Text>
              <strong>Input:</strong> alle gegevens, waaronder Persoonsgegevens,
              informatie, materialen of andere inhoud, in welke vorm dan ook,
              die door de Gebruiker wordt verstrekt, geüpload of ingevoerd in
              NOMAD360, al dan niet via derde(n) partijen, met als doel het
              genereren van Content;
            </Text>
            <Text>
              <strong>Lancewood AI Solutions:</strong> de besloten vennootschap
              met beperkte aansprakelijkheid Lancewood AI Solutions B.V.,
              gevestigd aan Westplein 12, 3016 BM te Rotterdam, Nederland, en
              geregistreerd bij de Kamer van Koophandel onder KvK-nummer:
              95481214;
            </Text>
            <Text>
              <strong>NOMAD360:</strong> de door Lancewood AI Solutions
              aangeboden softwareapplicatie, bereikbaar via nomad360.io en
              beschikbaar als mobiele Android- en iOS-applicatie, die fungeert
              als virtuele reisgids. NOMAD360 maakt gebruik van het AI-model om
              Gebruikers te ondersteunen bij het plannen en boeken van reizen;
            </Text>
            <Text>
              <strong>Persoonsgegevens:</strong> alle informatie over een
              geïdentificeerde of identificeerbare natuurlijke persoon,
              waaronder, maar niet uitsluitend, namen, adresgegevens, financiële
              gegevens en andere persoonlijke informatie die door de Gebruiker
              aan Lancewood AI Solutions wordt verstrekt via NOMAD360.
            </Text>

            {/* 3. Wijzigen */}
            <Heading as="h3" size="md">
              3. Wijzigen van deze algemene voorwaarden
            </Heading>
            <Text>
              3.1. Lancewood AI Solutions behoudt zich het recht voor om deze
              algemene voorwaarden te wijzigen. Eventuele wijzigingen worden ten
              minste 30 (dertig) dagen voor de inwerkingtreding bekendgemaakt
              via NOMAD360 en/of e-mail. Indien een wijziging een wezenlijke
              verandering in de dienstverlening inhoudt, heeft de Gebruiker het
              recht om de overeenkomst met Lancewood AI Solutions kosteloos te
              beëindigen. Door na de ingangsdatum van de wijzigingen gebruik te
              maken van NOMAD360, stemt de Gebruiker in met de gewijzigde
              voorwaarden.
            </Text>

            {/* 4. Overdraagbaarheid */}
            <Heading as="h3" size="md">
              4. Overdraagbaarheid
            </Heading>
            <Text>
              4.1. De rechten en verplichtingen die voortvloeien uit deze
              algemene voorwaarden zijn door Gebruiker niet aan derden
              overdraagbaar, behoudens uitdrukkelijke toestemming van Lancewood
              AI Solutions.
            </Text>

            {/* 5. Privacy */}
            <Heading as="h3" size="md">
              5. Privacy
            </Heading>
            <Text>
              5.1. De verwerking van Persoonsgegevens door Lancewood AI
              Solutions geschiedt in overeenstemming met de toepasselijke
              privacywetgeving en de privacyverklaring van Lancewood AI
              Solutions. Voor meer informatie over hoe Lancewood AI Solutions
              omgaat met de bescherming en verwerking van de Persoonsgegevens
              van Gebruikers wordt verwezen naar de privacyverklaring van
              Lancewood AI Solutions die te vinden is op{" "}
              <Link
                color="blue.500"
                href="https://nomad360.io/policies/privacy-policy/nl"
              >
                nomad360.io/policies/privacy-policy/nl
              </Link>
              . Gebruiker zal voorafgaan aan het gebruik van NOMAD360
              kennisnemen van deze privacyverklaring en eventuele bezwaren
              hiertegen kenbaar maken.
            </Text>

            {/* 6. Overmacht */}
            <Heading as="h3" size="md">
              6. Overmacht
            </Heading>
            <Text>
              6.1. Lancewood AI Solutions is niet aansprakelijk tegenover
              Gebruiker voor het niet nakomen van enige verplichting die
              voortvloeit uit deze algemene voorwaarden als gevolg van
              overmacht, waaronder, maar niet beperkt tot, natuurrampen,
              terrorisme, oorlog, politieke opstand, rebellie, rellen,
              burgerlijke onrust, handelingen van civiele of militaire
              autoriteiten, opstand, aardbeving, overstroming of ander
              natuurlijke of door de mens veroorzaakte gebeurtenissen buiten de
              controle van Lancewood AI Solutions die leiden de beëindiging van
              de dienstverlening, noch voor gebeurtenissen die redelijkerwijs
              niet voorzien hadden kunnen worden.
            </Text>
            <Text>
              6.2. Indien Lancewood AI Solutions door overmacht wordt getroffen,
              zal zij de Gebruiker hiervan zo spoedig mogelijk op de hoogte
              stellen en alle redelijke inspanning verrichten om te voldoen aan
              de verplichtingen die voortvloeien uit deze algemene voorwaarden.
            </Text>

            {/* 7. Aansprakelijkheid */}
            <Heading as="h3" size="md">
              7. Aansprakelijkheid
            </Heading>
            <Text>
              7.1. Lancewood AI Solutions is niet aansprakelijk voor enige
              directe schade die voortvloeit uit of verband houdt met deze
              algemene voorwaarden, ongeacht de rechtsgrond waarop de vordering
              is gebaseerd (contractuele aansprakelijkheid, onrechtmatige daad
              of anderszins).
            </Text>
            <Text>
              7.2. Indien, ondanks het bepaalde in artikel 7.1, Lancewood AI
              Solutions toch aansprakelijk wordt geacht voor enige directe
              schade, is deze aansprakelijkheid beperkt tot een maximumbedrag
              van € 10.000 per gebeurtenis, waarbij een reeks samenhangende
              gebeurtenissen als één gebeurtenis wordt beschouwd.
            </Text>
            <Text>
              7.3. Alle aansprakelijkheid van Lancewood AI Solutions voor andere
              schade dan directe schade is uitgesloten. Hieronder valt onder
              andere, maar niet uitsluitend, indirecte schade, gevolgschade,
              schade door een vertraagde levering, schade door vertraagde
              facturering, verlies en/of beschadiging van gegevens,
              omzetderving, winstderving en schade door een onderbreking in
              operationele werkzaamheden. Lancewood AI Solutions sluit specifiek
              schade uit voor:
            </Text>
            <Text pl={4}>
              a. Schade als gevolg van handelingen of nalatigheden van derde
              partijen (bijv. reisorganisaties, accommodaties,
              affiliate-partners);
              <br />
              b. Schade door onjuiste of onvolledige (reis)informatie of
              planningsfunctionaliteiten;
              <br />
              c. Schade voortkomend uit wijzigingen, annuleringen of nalatigheid
              van externe dienstverleners; en
              <br />
              d. Schade door storingen, onderbrekingen of beperkte
              toegankelijkheid van het platform.
            </Text>
            <Text>
              7.4. De in dit artikel opgenomen beperkingen van aansprakelijkheid
              komen te vervallen:
            </Text>
            <Text pl={4}>
              (i) ingeval van aanspraken op schadevergoeding ten gevolge van
              dood of letsel;
              <br />
              (ii) indien sprake is van opzet of bewuste roekeloosheid aan de
              zijde van Lancewood AI Solutions.
            </Text>
            <Text>
              7.5. Voorwaarde voor het ontstaan van enig recht op
              schadevergoeding is steeds dat Gebruiker de schade onverwijld, en
              uiterlijk binnen 14 dagen na het ontstaan daarvan, schriftelijk
              bij Lancewood AI Solutions meldt.
            </Text>
            <Text>
              7.6. Iedere vordering tot schadevergoeding tegen Lancewood AI
              Solutions vervalt door het enkele verloop van twaalf (12) maanden
              na het ontstaan van de (vermeende) vordering.
            </Text>
            <Text>
              7.7. Het bepaalde in dit artikel geldt ook ten gunste van alle
              (rechts)personen waarvan Lancewood AI Solutions zich ter
              uitvoering van NOMAD360 bedient.
            </Text>
            <Text>
              7.8. Lancewood AI Solutions is nimmer gehouden tot herstel van
              gegevens die zijn beschadigd of (deels) verloren zijn gegaan of
              vergoeding van schade veroorzaakt door beschadiging en/of het
              verloren gaan van gegevens.
            </Text>

            {/* 8. Toepasselijk recht */}
            <Heading as="h3" size="md">
              8. Toepasselijk recht en bevoegde rechter
            </Heading>
            <Text>
              8.1. Op deze algemene voorwaarden, enige overeenkomsten tussen
              partijen en daardoor beheerste rechtsverhoudingen is uitsluitend
              Nederlands recht van toepassing.
            </Text>
            <Text>
              8.2. Indien een tussen partijen bestaand geschil niet in onderling
              overleg kan worden beslecht, en voor zover uit de wet niet anders
              voortvloeit, is uitsluitend de Rechtbank Den Haag, locatie Den
              Haag bevoegd om over het geschil te beslissen.
            </Text>

            {/* HOOFDSTUK 2 */}
            <Heading as="h2" size="lg" mt={6}>
              HOOFDSTUK 2 – GEBRUIKSVOORWAARDEN NOMAD360
            </Heading>

            {/* 9. Toepassingsgebied */}
            <Heading as="h3" size="md">
              9. Toepassingsgebied
            </Heading>
            <Text>
              9.1. Deze gebruiksvoorwaarden zijn van toepassing op het gebruik
              van NOMAD360 in aanvulling op en, voor zover deze afwijken, in
              afwijking van de algemene bepalingen opgenomen in Hoofdstuk I.
            </Text>

            {/* 10. Registratie en Account */}
            <Heading as="h3" size="md">
              10. Registratie en Account
            </Heading>
            <Text>
              10.1. Voor gebruik van NOMAD360 en het afnemen van diensten dient
              de Gebruiker zich te registreren en een account aan te maken door
              het opgeven van een geldig e-mailadres en het kiezen van een
              wachtwoord, of door in te loggen via een geldig Google-account.
            </Text>
            <Text>
              10.2. Gebruiker is verplicht om bij het registreren en aanmaken
              van een Account naar waarheid te verklaren of hij/zij de leeftijd
              van 16 jaar of ouder heeft bereikt.
            </Text>
            <Text>
              10.3. Gebruiker is verplicht om zijn/haar gebruikersnamen en
              wachtwoorden geheim te houden. Het is niet toegestaan
              gebruikersnamen, wachtwoorden en/of eventueel verstrekte
              alternatieve authenticatiemiddelen over te dragen of aan derden in
              gebruik te geven.
            </Text>
            <Text>
              10.4. Lancewood AI Solutions is niet aansprakelijk voor misbruik
              van gebruikersnamen, wachtwoorden en/of eventueel verstrekte
              alternatieve authenticatiemiddelen en mag ervan uitgaan dat de
              persoon die zich aanmeldt met de gebruikersnaam, het wachtwoord
              van de Gebruiker en/of eventueel verstrekte alternatieve
              authenticatiemiddelen, ook daadwerkelijk die Gebruiker is.
            </Text>
            <Text>
              10.5. Zodra Gebruiker weet of reden heeft te vermoeden dat
              gebruikersnamen, wachtwoorden en/of eventueel verstrekte
              alternatieve authenticatiemiddelen in handen zijn gekomen van
              onbevoegden, moet Gebruiker Lancewood AI Solutions daarvan
              onverwijld op de hoogte stellen, onverminderd de eigen
              verplichting van Gebruiker om direct zelf doeltreffende
              maatregelen te treffen.
            </Text>

            {/* 11. Gebruiksrecht op NOMAD360 */}
            <Heading as="h3" size="md">
              11. Gebruiksrecht op NOMAD360
            </Heading>
            <Text>
              11.1. Door het accepteren van deze algemene voorwaarden en het
              aanmaken van een Account verstrekt Lancewood AI Solutions aan
              Gebruiker een beperkt, niet exclusief en niet-overdraagbaar recht
              om gebruik te maken van NOMAD360 conform de voorwaarden van deze
              algemene voorwaarden.
            </Text>
            <Text>
              11.2. Ten behoeve van het bepaalde in lid 1 zal Lancewood AI
              Solutions aan Gebruiker toegang verschaffen tot NOMAD360 en de
              Gebruikers Omgeving.
            </Text>

            {/* 12. Rechten en plichten van Gebruiker */}
            <Heading as="h3" size="md">
              12. Rechten en plichten van Gebruiker
            </Heading>
            <Text>
              12.1. Gebruiker zal NOMAD360 niet gebruiken voor handelingen die
              in strijd zijn met toepasselijke wet- en regelgeving, deze
              algemene voorwaarden en alle overige instructies, voorwaarden
              en/of aanwijzingen die door Lancewood AI Solutions van tijd tot
              tijd worden verstrekt of digitaal ter beschikking worden gesteld
              via NOMAD360.
            </Text>
            <Text>
              12.2. Gebruiker zal de software die wordt gebruikt voor of op
              NOMAD360 en/of andere diensten van Lancewood AI Solutions niet
              kopiëren, aanpassen, decompileren, vertalen of aanpassen, tenzij
              Lancewood AI Solutions hiervoor schriftelijke toestemming heeft
              gegeven of voor zover dit wettelijk dwingendrechtelijk is
              toegestaan.
            </Text>
            <Text>
              12.3. Gebruiker zal geen materiaal uploaden met een misleidende,
              beledigende, discriminerende of anderszins onrechtmatige – of naar
              mening van Lancewood AI Solutions – onbehoorlijke of onnodig
              krenkende inhoud.
            </Text>
            <Text>
              12.4. Gebruiker zal geen Input van derden uploaden zonder
              voorafgaande uitdrukkelijke toestemming van de betreffende derde.
              Indien de derde jonger is dan 16 jaar, zal Gebruiker vooraf de
              uitdrukkelijke toestemming verkrijgen van de ouder of wettelijk
              vertegenwoordiger van deze derde.
            </Text>
            <Text>
              12.5. Gebruiker zal in chatgroepen en de social community
              respectvol omgaan met andere gebruikers. Beledigingen,
              discriminatie, bedreigingen, intimidatie, haatzaaiende uitlatingen
              en andere vormen van ongewenst gedrag zijn strikt verboden.
            </Text>
            <Text>
              12.6. Het delen van schadelijke, gewelddadige, pornografische,
              misleidende, onjuiste of anderszins onrechtmatige inhoud is
              verboden.
            </Text>
            <Text>
              12.7. Gebruiker zal geen spam, commerciële promoties,
              kettingbrieven of andere vormen van ongewenste berichten delen met
              andere gebruikers.
            </Text>
            <Text>
              12.8. Gebruiker zal geen persoonlijke gegevens van zichzelf of
              anderen delen in chatgroepen of de social community, tenzij dit
              strikt noodzakelijk en in overeenstemming met de privacywetgeving
              is.
            </Text>
            <Text>
              12.9. Gebruiker zal geen misbruik maken van NOMAD360 door middel
              van hacking, spamming of andere vormen van misbruik.
            </Text>
            <Text>
              12.10. NOMAD360 is uitsluitend bedoeld voor persoonlijke,
              niet-commerciële doeleinden. Gebruikers mogen de Content enkel
              gebruiken voor privégebruik en mogen deze niet commercieel
              exploiteren zonder uitdrukkelijke toestemming van Lancewood AI
              Solutions.
            </Text>

            {/* 13. Rechten en plichten van Lancewood AI Solutions */}
            <Heading as="h3" size="md">
              13. Rechten en plichten van Lancewood AI Solutions
            </Heading>
            <Text>
              13.1. Mocht Lancewood AI Solutions kennis hebben of tot het besef
              komen dat Input en/of Content die Gebruiker met behulp van of via
              NOMAD360 heeft opgeslagen en/of geüpload, onrechtmatig is of
              aanleiding zou kunnen geven tot civiele of strafrechtelijke
              aansprakelijkheid van Lancewood AI Solutions, dan is Lancewood AI
              Solutions gerechtigd om dit materiaal of deze gegevens per direct
              te verwijderen of de toegang daartoe onmogelijk te maken. In geen
              geval zal Lancewood AI Solutions aansprakelijk zijn voor schade
              die voortvloeit uit dat handelen.
            </Text>
            <Text>
              13.2. Lancewood AI Solutions behoudt zich het recht voor om het
              Account van Gebruiker zonder vooraankondiging te sluiten, indien
              Gebruiker zich niet houdt aan het bepaalde in deze algemene
              voorwaarden.
            </Text>

            {/* 14. Garantie ten aanzien van NOMAD360 */}
            <Heading as="h3" size="md">
              14. Garantie ten aanzien van NOMAD360
            </Heading>
            <Text>
              14.1. NOMAD360 wordt geleverd "as is" en “as available”. Lancewood
              AI Solutions garandeert enkel dat NOMAD360 is ontwikkeld en ter
              beschikking gesteld in overeenstemming met de toepasselijke
              standaarden in de IT-sector. De Gebruiker erkent en stemt ermee in
              dat NOMAD360 wordt geleverd door Lancewood AI Solutions met geen
              andere garanties of verplichtingen dan die in deze algemene
              voorwaarden.
            </Text>

            {/* 15. Statistieken */}
            <Heading as="h3" size="md">
              15. Statistieken
            </Heading>
            <Text>
              15.1. Gebruiker begrijpt en stemt ermee in dat NOMAD360 technische
              en gebruiksanalyses (“Statistieken”) verzendt naar Lancewood AI
              Solutions en, indien van toepassing, naar Lancewood AI’s
              licentiegevers en/of hun gelieerde ondernemingen en
              dienstverleners. Gebruiker gaat ermee akkoord dat Lancewood AI
              Solutions en/of haar licentiegevers en gelieerde partijen, voor
              zichzelf of derden:
            </Text>
            <Text pl={4}>
              a) Statistieken verzamelen, opslaan en gebruiken; en
              <br />
              b) Deze Statistieken overdragen voor de volgende doeleinden:
              <br />
              (i) het leveren van software en andere diensten,
              <br />
              (ii) het mogelijk maken van nieuwe software, updates,
              verbeteringen en andere diensten,
              <br />
              (iii) het verbeteren van de bestaande NOMAD360, software, andere
              diensten en technologieën.
            </Text>

            {/* 16. Intellectuele eigendom NOMAD360 */}
            <Heading as="h3" size="md">
              16. Intellectuele eigendom NOMAD360
            </Heading>
            <Text>
              16.1. Tenzij schriftelijk anders overeengekomen, berusten alle
              intellectuele eigendomsrechten met betrekking tot NOMAD360,
              waaronder maar niet beperkt tot octrooirechten, auteursrechten,
              merkrechten en databankrechten, bij Lancewood AI Solutions, haar
              toeleveranciers of haar licentiegevers. De Gebruiker verkrijgt
              uitsluitend de gebruiksrechten die uitdrukkelijk worden toegekend
              in deze algemene voorwaarden of op basis van de wet. Alleen
              Lancewood AI Solutions is bevoegd om de benodigde aanvragen en
              registraties voor de bescherming van deze rechten in te dienen.
            </Text>
            <Text>
              16.2. Indien de in lid 1 genoemde (toekomstige) rechten die
              voortvloeien uit deze algemene voorwaarden bij de Gebruiker komen
              te rusten, zal de Gebruiker meewerken aan de overdracht van deze
              rechten aan Lancewood AI Solutions. Deze medewerking kan onder
              andere, maar niet uitsluitend, bestaan uit het aangaan van een
              overeenkomst. Indien wettelijk vereist, zal de Gebruiker op eerste
              verzoek van Lancewood AI Solutions meewerken aan de vereiste
              formaliteiten voor de overdracht van de betreffende rechten.
            </Text>

            {/* HOOFDSTUK 3 */}
            <Heading as="h2" size="lg" mt={6}>
              HOOFDSTUK 3 – SPECIFIEKE BEPALINGEN TEN AANZIEN VAN INPUT EN
              CONTENT
            </Heading>

            {/* 17. Toepassingsgebied */}
            <Heading as="h3" size="md">
              17. Toepassingsgebied
            </Heading>
            <Text>
              17.1. De bepalingen in dit hoofdstuk zijn van toepassing op de
              Input en Content die via NOMAD360 wordt gegenereerd of geüpload.
              Deze bepalingen gelden in aanvulling op, en waar nodig in
              afwijking van, de algemene bepalingen uit Hoofdstuk I.
            </Text>

            {/* 18. Garantie ten aanzien van Content */}
            <Heading as="h3" size="md">
              18. Garantie ten aanzien van Content
            </Heading>
            <Text>
              18.1. De via NOMAD360 gegenereerde Content is gebaseerd op de
              Input van de Gebruiker en op gegevens van externe leveranciers,
              zoals databrokers. Lancewood AI Solutions garandeert niet de
              juistheid, volledigheid, actualiteit of geschiktheid van de
              Content, waaronder, maar niet beperkt tot, realtime informatie
              over vliegtickets, hotels en reisservices van externe
              data-aanbieders. De Gebruiker is zelf verantwoordelijk voor het
              verifiëren en beoordelen van de gegenereerde Content voordat hij
              deze gebruikt of daarop vertrouwt.
            </Text>
            <Text>
              18.2. De Gebruiker begrijpt en erkent dat de Content van NOMAD360
              wordt gegenereerd door een AI-model met inherente beperkingen. Het
              AI-model kan fouten bevatten, context verkeerd interpreteren of
              onnauwkeurige informatie genereren. Lancewood AI Solutions streeft
              ernaar om nauwkeurige en betrouwbare Content te leveren, maar
              staat niet in voor de foutloosheid of volledigheid ervan. De
              Gebruiker wordt geadviseerd de gegenereerde Content zorgvuldig te
              evalueren en, indien nodig, aanvullende verificatie uit te voeren
              of professioneel advies in te winnen.
            </Text>
            <Text>
              18.3. Gebruiker wordt erop gewezen dat reisadviezen of -suggesties
              van NOMAD360 mogelijk niet overeenkomen met de actuele
              veiligheidssituatie op bepaalde bestemmingen. Lancewood AI
              Solutions raadt de Gebruiker aan om altijd de officiële
              reisadviezen van overheidsinstanties, zoals het Ministerie van
              Buitenlandse Zaken, te raadplegen. Lancewood AI Solutions is niet
              verantwoordelijk voor de gevolgen van een onjuiste inschatting van
              de veiligheidssituatie, en de Gebruiker wordt geacht om
              zelfstandige verificatie uit te voeren aan de hand van officiële
              bronnen. Lancewood AI Solutions aanvaardt geen aansprakelijkheid
              voor schade of nadelige gevolgen die voortvloeien uit het opvolgen
              van adviezen die afwijken van officiële veiligheidsrichtlijnen.
            </Text>
            <Text>
              18.4. Lancewood AI Solutions sluit iedere aansprakelijkheid uit
              voor directe, indirecte of gevolgschade die voortvloeit uit het
              gebruik van de Content. Dit omvat onder meer schade als gevolg van
              onjuiste of onvolledige informatie van externe leveranciers,
              beslissingen die zijn gebaseerd op door NOMAD360 verstrekte
              adviezen, of fouten voortkomend uit verwijzingen naar externe
              diensten.
            </Text>

            {/* 19. Verwijzingen naar websites van derden */}
            <Heading as="h3" size="md">
              19. Verwijzingen naar websites van derden
            </Heading>
            <Text>
              19.1. NOMAD360 kan verwijzingen bevatten naar websites, platforms
              of diensten van derden. Deze verwijzingen worden uitsluitend ter
              informatie en voor het gemak van de Gebruiker verstrekt. Het
              opnemen van dergelijke links impliceert geen goedkeuring of
              aanbeveling van de inhoud, producten of diensten die op deze
              websites worden aangeboden door Lancewood AI Solutions.
            </Text>
            <Text>
              19.2. Lancewood AI Solutions heeft geen controle over en is niet
              verantwoordelijk voor de beschikbaarheid, nauwkeurigheid,
              rechtmatigheid, of inhoud van websites van derden, noch voor de
              producten, diensten of informatie die via deze websites worden
              verstrekt. Het gebruik van dergelijke externe websites is volledig
              op eigen risico van de Gebruiker.
            </Text>
            <Text>
              19.3. De Gebruiker dient zich ervan bewust te zijn dat bij het
              bezoeken van websites van derden de voorwaarden en het
              privacybeleid van die websites van toepassing zijn. Lancewood AI
              Solutions adviseert de Gebruiker om deze voorwaarden en het beleid
              zorgvuldig door te lezen alvorens gebruik te maken van de diensten
              van derden.
            </Text>
            <Text>
              19.4. Lancewood AI Solutions aanvaardt geen aansprakelijkheid voor
              schade of nadelige gevolgen die voortvloeien uit het gebruik van
              of vertrouwen op inhoud, producten of diensten die via websites
              van derden worden aangeboden. De verantwoordelijkheid voor het
              nemen van voorzorgsmaatregelen, zoals het controleren van de
              betrouwbaarheid en veiligheid van deze externe bronnen, ligt
              volledig bij de Gebruiker.
            </Text>

            {/* 20. Intellectuele eigendom Input en Content */}
            <Heading as="h3" size="md">
              20. Intellectuele eigendom Input en Content
            </Heading>
            <Text>
              20.1. De Gebruiker verleent Lancewood AI Solutions een beperkte,
              niet-exclusieve en niet overdraagbare licentie om toegang te
              verkrijgen tot, gebruik te maken van, op te slaan, te
              reproduceren, te verzenden, weer te geven, te publiceren te
              distribueren en te bewerken van de Input, uitsluitend voor het
              genereren van de Content.
            </Text>
            <Text>
              20.2. Gebruiker garandeert dat hij/zij gemachtigd is om de Input
              die hij/zij in het kader van de dienstverlening aan Lancewood AI
              Solutions ter beschikking stelt te verstrekken. Tevens verklaart
              Gebruiker dat de Input geen inbreuk maken op rechten van derden,
              en vrijwaart Lancewood AI Solutions hierbij tegen alle eventuele
              claims, aansprakelijkheden, kosten en schade die voortvloeien uit
              dergelijke inbreuken.
            </Text>
            <Text>
              20.3. Voor zover er op de door de Gebruiker via NOMAD360
              gegenereerde Content intellectuele eigendomsrechten rusten,
              blijven deze rechten eigendom van de Gebruiker. Lancewood AI
              Solutions claimt geen eigendomsrechten op dergelijke Content. De
              Gebruiker verleent Lancewood AI Solutions echter een licentie om
              deze Content (voor zover het persoonsgegevens betreffen in
              geanonimiseerde vorm) te gebruiken, wijzigen en distribueren, met
              het oog op het verbeteren van NOMAD360 en het AI-model.
            </Text>
          </VStack>
        </Box>
        <JoinCommunitySection />
        <Footer />
        <BackToTopButton />
      </Box>
    </>
  );
};

export default TermsOfServiceNL;
